import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/super-admin', icon: '📊', label: 'Dashboard' },
  { to: '/super-admin/businesses', icon: '🏢', label: 'Businesses' },
  { to: '/super-admin/payments', icon: '💰', label: 'Payments' },
  { to: '/super-admin/invoice-report', icon: '🧾', label: 'Invoice Report' },
  { to: '/super-admin/tickets', icon: '🎫', label: 'Support Tickets' },

  { to: '/super-admin/plans', icon: '📦', label: 'Plans' },
  { to: '/super-admin/agents', icon: '👥', label: 'Agent Requests' }
];

const STATUS_COLORS = { approved: '#00d55b', pending: '#f59e0b', rejected: '#ff4444' };

export default function InvoiceReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [month, setMonth] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => { load(); }, [status, month]);

  const load = () => {
    setLoading(true);
    let url = `/api/super-admin/invoice-report?status=${status}`;
    if (month) url += `&month=${month}`;
    axios.get(url).then(r => {
      setRows(r.data.data || []);
      setTotal(r.data.total || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const exportCSV = () => {
    const token = localStorage.getItem('mschat_token');
    let url = `https://api.mschat.msdiscoveries.com/api/super-admin/invoice-report?format=csv&status=${status}&token=${token}`;
    if (month) url += `&month=${month}`;
    window.open(url, '_blank');
    toast.success('CSV downloading...');
  };

  const totalAmount = (currency) => rows
    .filter(r => r.currency === currency && r.payment_status === 'approved')
    .reduce((sum, r) => sum + Number(r.amount), 0);

  return (
    <DashLayout navItems={nav} role="Super Admin">
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>Invoice Report</h1>
            <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Payment records for auditor</p>
          </div>
          <button onClick={exportCSV}
            style={{ padding: '10px 20px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}>
            📥 Export CSV
          </button>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
          {[
            ['Total Records', total, '#fff'],
            ['Total INR (Paid)', '₹' + totalAmount('inr').toLocaleString('en-IN'), '#00d55b'],
            ['Total USD (Paid)', '$' + totalAmount('usd').toFixed(2), '#00d55b'],
            ['Pending', rows.filter(r => r.payment_status === 'pending').length, '#f59e0b']
          ].map(([label, val, color]) => (
            <div key={label} style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <select value={status} onChange={e => setStatus(e.target.value)}
            style={{ padding: '9px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13 }}>
            <option value="all">All Status</option>
            <option value="approved">✅ Paid</option>
            <option value="pending">⏳ Pending</option>
            <option value="rejected">❌ Rejected</option>
          </select>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            style={{ padding: '9px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13 }} />
          {month && <button onClick={() => setMonth('')}
            style={{ padding: '9px 14px', background: '#1a1a1a', border: '1px solid #1e1e1e', borderRadius: 8, color: '#888', cursor: 'pointer', fontSize: 13 }}>
            Clear
          </button>}
        </div>

        {/* Table */}
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0d0d0d', borderBottom: '1px solid #1e1e1e' }}>
                {['Invoice No', 'Company', 'Plan', 'Amount', 'GST Type', 'Status', 'Date', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, color: '#555', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#333' }}>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#333' }}>No records found</td></tr>
              ) : rows.map(row => {
                const isINR = row.currency === 'inr';
                const isTN = row.state?.toLowerCase() === 'tamil nadu';
                const gst = isINR ? (isTN ? 'CGST+SGST 18%' : 'IGST 18%') : 'N/A';
                const sym = isINR ? '₹' : '$';
                return (
                  <tr key={row.id} style={{ borderBottom: '1px solid #0d0d0d' }}>
                    <td style={{ padding: '12px 14px', fontSize: 12, fontFamily: 'monospace', color: '#00d55b' }}>{row.invoice_no}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{row.company_name}</div>
                      <div style={{ fontSize: 11, color: '#555' }}>{row.email}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#888', textTransform: 'capitalize' }}>{row.plan_name}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: '#fff' }}>{sym}{Number(row.amount).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', fontSize: 11, color: '#888' }}>{gst}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: `${STATUS_COLORS[row.payment_status]}22`, color: STATUS_COLORS[row.payment_status], fontWeight: 600 }}>
                        {row.payment_status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#555' }}>
                      {new Date(row.submitted_at).toLocaleDateString('en-IN')}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <a href={`https://api.mschat.msdiscoveries.com/api/payment/invoice/${row.id}`} target="_blank" rel="noreferrer"
                        style={{ padding: '5px 12px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 12, textDecoration: 'none' }}>
                        View
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashLayout>
  );
}